import { useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Lock, Pin, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAutosave } from "@/lib/autosave";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { RichEditor, SaveIndicator } from "./RichEditor";

export const newNote = () => ({ titolo: "", data: format(new Date(), "yyyy-MM-dd"), body_html: "", pinned: false });

const NoteBody = ({ clientId, initial, onChanged, onDeleted }) => {
  const [id, setId] = useState(initial.id || null);
  const [f, setF] = useState({ titolo: initial.titolo, data: initial.data, body_html: initial.body_html, pinned: initial.pinned });
  const [del, setDel] = useState(false);
  const creating = useRef(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const { status } = useAutosave(f, async (d) => {
    if (!d.titolo.trim() || !d.data) return false;
    const body = { ...d, titolo: d.titolo.trim() };
    if (!id && !creating.current) {
      creating.current = api.post(`/me/clienti/${clientId}/appunti`, body).then((r) => { setId(r.data.id); onChanged(r.data); return r.data.id; });
      await creating.current;
      return true;
    }
    const realId = id || (await creating.current);
    onChanged((await api.put(`/me/appunti/${realId}`, body)).data);
    return true;
  });
  const remove = async () => {
    try { await api.delete(`/me/appunti/${id}`); toast.success("Appunto eliminato"); onDeleted(id); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between pr-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#E8173A]"><Lock className="h-3.5 w-3.5" />Appunto privato · visibile solo a te</span>
        <SaveIndicator status={status} testid="note-save-status" />
      </div>
      <Input autoFocus value={f.titolo} onChange={set("titolo")} placeholder="Titolo dell'appunto" className="rounded-xl text-xl font-extrabold h-12" data-testid="note-title" />
      <div className="flex items-center gap-4">
        <Input type="date" value={f.data} onChange={set("data")} className="rounded-xl w-48" data-testid="note-date" />
        <button type="button" onClick={() => set("pinned")(!f.pinned)} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold ${f.pinned ? "bg-[#0A1128] text-white" : "bg-[#A6DAFC] text-[#0A1128]"}`} data-testid="note-pin"><Pin className="h-4 w-4" />{f.pinned ? "Fissato in alto" : "Fissa in alto"}</button>
      </div>
      <RichEditor value={f.body_html} onChange={set("body_html")} minHeight={480} testid="note-body" />
      {id && <div><Button variant="outline" onClick={() => setDel(true)} className="rounded-full font-bold text-[#E8173A] border-red-200" data-testid="note-delete"><Trash2 className="h-4 w-4 mr-1.5" />Elimina appunto</Button></div>}
      <ConfirmDialog open={del} onOpenChange={setDel} title="Eliminare l'appunto?" description="L'appunto verrà eliminato definitivamente." onConfirm={remove} />
    </div>
  );
};

export const NotePanel = ({ panel, clientId, onClose, onChanged, onDeleted }) => (
  <Sheet open={!!panel} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full sm:max-w-[960px] bg-[#8FD0FB] overflow-y-auto core-scroll" data-testid="note-panel">
      <SheetTitle className="sr-only">Appunto privato</SheetTitle>
      <SheetDescription className="sr-only">Modifica il tuo appunto privato sul cliente</SheetDescription>
      {panel && <NoteBody key={panel.key} clientId={clientId} initial={panel.note} onChanged={onChanged} onDeleted={(id) => { onDeleted(id); onClose(); }} />}
    </SheetContent>
  </Sheet>
);
